import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle } from 'lucide-react'

export type ConfirmDialogTone = 'danger' | 'default'

interface ConfirmDialogProps {
  open: boolean
  /** Short headline — avoid "Are you sure?"; describe the outcome. */
  title: string
  /** Optional paragraph — what the action will do, what is irreversible, etc. */
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  tone?: ConfirmDialogTone
  /** True while the confirm handler is in flight — disables buttons + shows spinner. */
  loading?: boolean
  onConfirm: () => void | Promise<void>
  onClose: () => void
}

/**
 * Themed replacement for {@code window.confirm}. Matches the modal pattern
 * already in use elsewhere in idfy-ui (PersonaSetup's model-upgrade dialog,
 * AccountModal): backdrop click closes, Escape closes, focus is trapped
 * inside the panel and restored on close.
 */
export default function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  loading = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const previouslyFocused = document.activeElement as HTMLElement | null
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return }
      if (e.key !== 'Tab') return
      const items = tabbables(panelRef.current)
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]
      if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault() }
      else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault() }
    }
    window.addEventListener('keydown', onKey)
    // Focus the cancel button by default — safer default for destructive prompts.
    const t = window.setTimeout(() => {
      const cancelBtn = panelRef.current?.querySelector<HTMLElement>('[data-confirm-cancel]')
      cancelBtn?.focus()
    }, 0)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.clearTimeout(t)
      previouslyFocused?.focus?.()
    }
  }, [open, onClose])

  if (!open) return null
  // SSR guard — react-hot-toast does the same.
  if (typeof document === 'undefined') return null

  const isDanger = tone === 'danger'
  const confirmClasses = isDanger
    ? 'inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
    : 'btn-primary text-sm'
  const iconWrapClasses = isDanger
    ? 'w-10 h-10 rounded-lg bg-red-500/15 text-red-500 dark:text-red-400 flex items-center justify-center flex-shrink-0'
    : 'w-10 h-10 rounded-lg bg-brand-500/15 text-brand-400 flex items-center justify-center flex-shrink-0'

  return createPortal((
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      aria-describedby={description ? 'confirm-description' : undefined}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
    >
      <div className="modal-backdrop" onClick={loading ? undefined : onClose} aria-hidden="true" />
      <div ref={panelRef} className="modal-panel relative w-full max-w-lg rounded-2xl">
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className={iconWrapClasses}>
              <AlertTriangle size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 id="confirm-title" className="text-[15px] font-semibold text-gray-900 dark:text-gray-100">
                {title}
              </h3>
              {description && (
                <p id="confirm-description" className="text-sm text-[rgb(var(--text-muted))] mt-1">
                  {description}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              data-confirm-cancel
              className="btn-secondary text-sm">
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={() => { void onConfirm() }}
              disabled={loading}
              className={confirmClasses}>
              {loading && (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              )}
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  ), document.body)
}

function tabbables(container: HTMLElement | null): HTMLElement[] {
  if (!container) return []
  const sel = [
    'a[href]', 'area[href]', 'input:not([disabled])', 'select:not([disabled])',
    'textarea:not([disabled])', 'button:not([disabled])', '[tabindex]:not([tabindex="-1"])',
  ].join(',')
  return Array.from(container.querySelectorAll<HTMLElement>(sel))
    .filter((el) => !el.hasAttribute('aria-hidden') && el.offsetParent !== null)
}
